/**
 * sessionPdfDocument.js : génération du PDF de session, sources injectées.
 *
 * Extrait de PDFExport.jsx (10.5.323, D247) pour servir deux pages avec un
 * seul générateur : la fiche session (sources du propriétaire, routes
 * authentifiées) et la page du lien de séance (sources publiques : ni
 * annotations ni photos, modèles du résumé lus sous le jeton, mention du lien
 * en pied de page). Le flux et les sections du PDF du propriétaire sont ceux
 * de PDFExport avant extraction.
 *
 * Période analysée (D240) : `period` est l'objet de buildPeriodAnalysis déjà
 * calculé pour l'écran, jamais recalculé ici.
 */
import jsPDF from 'jspdf';
import * as Sentry from '@sentry/react';
import { getAuthHeaders } from '../utils/authFetch';
import { formatHumanDuration } from '../utils/formatDuration';
import { formatDateLocale } from '../utils/formatters';
import {
  drawHeader, drawSectionTitle, drawInfoTable, checkPageBreak, buildPdfFilename,
} from './pdfHelpers';
import { drawStatsTable, drawSummary } from './pdfTables';
import {
  drawAnnotationsList, drawRecommendationsSection, drawVetNotes, drawFooters,
} from './pdfSections';
import {
  drawThresholdsTable, drawAucSection, drawSessionCharts, injectChartBackgroundOverride,
} from './pdfCharts';
import { drawSessionPhotos } from './pdfPhotos';
import { captureMapCanvas } from './pdfMapCapture';
import { annotationsInPeriod, drawPeriodHeader, pdfText, periodFilenameSuffix } from './pdfPeriod';
import { sensorLabelLong, sensorIdentifier } from '../constants/sensorLabels';
import { generateSummary } from '../sessions/sessionSummaryGenerator';
import defaultTemplates from '../constants/sessionSummaryTemplates.json';
import summaryTemplatesEn from '../constants/sessionSummaryTemplates.en.json';

/**
 * Sources de la fiche session (routes authentifiées).
 *   loadAnnotations : corps de GET /annotations, ou null pour omettre la section ;
 *   loadFrTemplates : modèles FR (surcharge super_admin honorée) ;
 *   photos : section photos ; footerMention : texte en pied de page.
 */
export function ownerPdfSources(sessionId) {
  return {
    loadAnnotations: async () => {
      const res = await fetch(`/api/sessions/${sessionId}/annotations`, { headers: getAuthHeaders() });
      return res.json();
    },
    loadFrTemplates: () => fetch('/api/sessions/summary-templates', {
      credentials: 'include',
      headers: getAuthHeaders(),
    }).then(r => r.json()).then(d => d.success ? d.templates : defaultTemplates).catch(() => defaultTemplates),
    photos: true,
    footerMention: undefined,
  };
}

/**
 * @returns {Promise<string[]>} avertissements (sections incomplètes) ; rejette
 *   sur un échec global (aucun PDF enregistré).
 */
export async function generateSessionPdf({ session, sessionId, readings = [], period = null, t, language, onProgress, sources }) {
  const warnings = [];
  onProgress(t('pdf.progress.preparing'));
  // Fond blanc forcé sur les canvas capturés pendant l'export (pdfCharts).
  const removeOverride = injectChartBackgroundOverride();
  // Données chiffrées : la période de l'écran si elle est active.
  const data = period ? period.session : session;
  const dataReadings = period ? period.readings : readings;
  try {
    const pdf         = new jsPDF('p', 'mm', 'a4');
    const pageWidth   = pdf.internal.pageSize.getWidth();
    const pageHeight  = pdf.internal.pageSize.getHeight();
    const margin      = 15;
    const contentWidth = pageWidth - 2 * margin;

    // ─── EN-TÊTE avec logo ────────────────────────────────────────────────
    let yPos = drawHeader(pdf, pageWidth);

    // ─── PÉRIODE ANALYSÉE (mêmes phrases que le bandeau de l'écran) ───────
    if (period) yPos = drawPeriodHeader(pdf, period, yPos, margin, contentWidth, pageHeight);

    // ─── INFORMATIONS DE SESSION ──────────────────────────────────────────
    yPos = drawSectionTitle(pdf, t('pdf.section.sessionInfo'), yPos, margin);

    const sessionDate = formatDateLocale(session.start_time, {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    const horseName   = session.horseName || session.horse_id?.horseName || t('pdf.info.notProvided');

    const infoRows = [
      { label: t('pdf.info.horse'),   value: horseName },
      { label: t('pdf.info.session'),  value: session.title || t('common.notProvided') },
      { label: t('pdf.info.date'),     value: sessionDate },
      { label: t('pdf.info.duration'),    value: formatHumanDuration(
        session.end_time && session.start_time
          ? new Date(session.end_time) - new Date(session.start_time)
          : null
      ) },
      { label: t('pdf.info.sensor'),  value: sensorLabelLong(session, t) },
      ...[sensorIdentifier(session, t)].filter(Boolean),
      { label: t('pdf.info.measurements'),  value: session.data_count
        ? t('pdf.info.dataPoints', { count: session.data_count })
        : t('common.notProvided') },
    ];

    if (session.departure && session.arrival) {
      infoRows.push({ label: t('pdf.info.route'), value: pdfText(`${session.departure} → ${session.arrival}`) });
    } else if (session.location?.city) {
      infoRows.push({ label: t('pdf.info.place'), value: session.location.address || session.location.city });
    }

    yPos = drawInfoTable(pdf, infoRows, yPos, margin, contentWidth);
    yPos += 5;

    // ─── ANNOTATIONS / ACTIVITÉS ──────────────────────────────────────────
    if (sources.loadAnnotations) {
      try {
        const annData = await sources.loadAnnotations();
        if (!annData.success) warnings.push(t('pdf.warning.annotationsUnavailable'));

        // Avec une période : seulement les annotations qui y commencent.
        const shown = annData.success && period
          ? annotationsInPeriod(annData.annotations, period, new Date(session.start_time).getTime())
          : annData.annotations;
        if (annData.success && shown?.length > 0) {
          yPos = drawAnnotationsList(pdf, shown, yPos, margin, pageHeight);
        }
      } catch (err) {
        // Annotations optionnelles : le PDF sort sans cette section, avertissement affiché.
        Sentry.captureException(err, { extra: { stage: 'pdf_annotations' } });
        warnings.push(t('pdf.warning.annotationsUnavailable'));
      }
    }

    // ─── STATISTIQUES PM ──────────────────────────────────────────────────
    // P95 et temps au-dessus des seuils sont ceux des cartes de l'écran
    // (period.exposure avec une période, même calcul sans, D241), P95
    // masqué sous 30 mesures. Hauteur réservée : tableau et légende.
    onProgress(t('pdf.progress.stats'));
    yPos = checkPageBreak(pdf, yPos, 85, margin, pageHeight);
    yPos = drawSectionTitle(pdf, t('pdf.section.statistics'), yPos, margin);
    yPos = drawStatsTable(pdf, data, dataReadings, yPos, margin, pageWidth,
      period ? { exposure: period.exposure, hiddenP95: period.hiddenP95 } : undefined);

    // ─── RÉSUMÉ EN LANGAGE NATUREL ──────────────────────────────────────────
    // Option C (1.4b-3) : en EN, templates depuis le JSON front-only ; en FR,
    // modèles de la source (override super_admin honoré, défaut front sinon).
    const summaryTemplates = language?.startsWith('en')
      ? summaryTemplatesEn
      : await sources.loadFrTemplates();

    const summarySentences = generateSummary(data, dataReadings, summaryTemplates,
      { scope: period ? 'period' : 'session' });
    // Titre porté par l'encadré lui-même (drawSummary) : pas de titre de section.
    if (summarySentences.length > 0) {
      yPos = checkPageBreak(pdf, yPos, 40, margin, pageHeight);
      yPos = drawSummary(pdf, summarySentences, yPos, margin, contentWidth, pageHeight);
    }

    // ─── SEUILS DE RÉFÉRENCE ──────────────────────────────────────────────
    yPos = drawThresholdsTable(pdf, yPos, margin, contentWidth, pageHeight);

    // ─── EXPOSITION CUMULÉE (AUC) ───────────────────────────────────────────
    yPos = drawAucSection(pdf, yPos, data, dataReadings, margin, contentWidth, pageHeight,
      { periodDurationMs: period?.durationMs });

    // ─── RECOMMANDATIONS ──────────────────────────────────────────────────
    yPos = drawRecommendationsSection(pdf, data, dataReadings, yPos, margin, pageWidth, pageHeight);

    // ─── OBSERVATIONS VÉTÉRINAIRES ────────────────────────────────────────
    if (session.vetNotes?.content) {
      yPos = checkPageBreak(pdf, yPos, 40, margin, pageHeight);
      yPos = drawVetNotes(pdf, session.vetNotes, yPos, margin, pageWidth);
      yPos += 5;
    }

    // ─── GRAPHIQUES ───────────────────────────────────────────────────────
    // Canvas de l'écran, déjà limités à la période ; zoom remis à zéro pendant
    // la capture (drawSessionCharts).
    onProgress(t('pdf.progress.charts'));
    yPos = await drawSessionCharts(pdf, yPos, margin, contentWidth, pageWidth, pageHeight, dataReadings);

    // ─── PHOTOS ───────────────────────────────────────────────────────────
    // Optionnelles : le PDF sort sans elles, mais tout manque est signalé.
    // Une photo ne porte pas l'heure de sa prise de vue : avec une période,
    // toutes les photos de la session restent, et le titre le dit.
    if (sources.photos) {
      onProgress(t('pdf.progress.photos'));
      const photos = await drawSessionPhotos(pdf, {
        sessionId, title: period ? t('pdf.period.photosTitle') : t('photos.title'),
        yPos, margin, contentWidth, pageWidth, pageHeight,
      });
      yPos = photos.yPos;
      if (photos.unavailable) warnings.push(t('pdf.warning.photosUnavailable'));
      if (photos.skipped > 0) warnings.push(t('pdf.warning.photosSkipped', { count: photos.skipped }));
    }

    // ─── CARTE ────────────────────────────────────────────────────────────
    const mapEl = document.querySelector('.leaflet-container');
    if (mapEl) {
      try {
        yPos = checkPageBreak(pdf, yPos, 80, margin, pageHeight);
        yPos = drawSectionTitle(pdf, t('location.title.single'), yPos, margin);
        await new Promise(r => setTimeout(r, 1500));
        const mapCanvas = await captureMapCanvas(mapEl, {
          scale: 1.2, useCORS: true, logging: false, backgroundColor: '#fff',
        });
        const mapImg    = mapCanvas.toDataURL('image/jpeg', 0.80);
        const mapH      = (mapCanvas.height * contentWidth) / mapCanvas.width;
        yPos = checkPageBreak(pdf, yPos, mapH + 5, margin, pageHeight);
        pdf.addImage(mapImg, 'JPEG', margin, yPos, contentWidth, mapH);
      } catch (err) {
        // Carte optionnelle : le PDF sort sans cette section.
        Sentry.captureException(err, { extra: { stage: 'pdf_map' } });
      }
    }

    // ─── FOOTERS ──────────────────────────────────────────────────────────
    drawFooters(pdf, pageWidth, pageHeight, sources.footerMention);

    onProgress(t('pdf.progress.finalizing'));
    // ─── SAVE ─────────────────────────────────────────────────────────────
    // Nom de fichier langue-dépendant : EkynAir-<Rapport|report>-[Cheval]-[Titre].pdf
    // (slug via i18n, structure préservée — cf. buildPdfFilename), suffixé par la
    // période si elle est active. La date/heure de génération est dans le header
    // du PDF (valeur légale équivalente).
    pdf.save(buildPdfFilename(horseName, session.title, period ? periodFilenameSuffix(period) : undefined));
    return warnings;
  } finally {
    removeOverride();
  }
}
